import { RedoButton } from "@redotech/arbiter/buttons/redo-button";
import { Card } from "@redotech/arbiter/card";
import { Flex } from "@redotech/arbiter/flex";
import { RedoIncrementDecrement } from "@redotech/arbiter/increment-decrement/redo-increment-decrement";
import { RedoRadioGroup } from "@redotech/arbiter/radio/redo-radio-group";
import { Text } from "@redotech/arbiter/text";
import { RedoToggle } from "@redotech/arbiter/toggle/redo-toggle";
import { DisabledTooltip } from "@redotech/arbiter/tooltip/disabled-tooltip";
import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import { ORDER_TRACKING_BILLING_DEFAULTS } from "@redotech/redo-billing-common/billing-defaults";
import { PaymentProviderType } from "@redotech/redo-billing-common/billing-engine-types";
import { BillingType } from "@redotech/redo-billing-common/billing-window-config-definition";
import {
  BillingPeriod,
  billingPeriodToFriendlyUpper,
  ServiceInvoiceType,
} from "@redotech/redo-billing-common/service-invoice";
import { teamGetPaymentMethod } from "@redotech/redo-team-common/team";
import { toast } from "@redotech/redo-web/alert";
import { DateInput } from "@redotech/redo-web/date-picker";
import * as gridCss from "@redotech/redo-web/grid.module.css";
import { LabeledInput } from "@redotech/redo-web/labeled-input";
import { useRpcQuery } from "@redotech/redo-web/rpc-query/use-rpc-query";
import { groupInput, input, InputSchema } from "@redotech/ui/form";
import {
  areDatesEqual,
  getDateStartOfDayInTimezone,
} from "@redotech/util/date";
import { assertNever } from "@redotech/util/type";
import { memo, useContext, useEffect, useMemo, useState } from "react";
import { RedoAdminRpcClientContext } from "../../../app/redo-admin-rpc-client-provider";
import { useHasMarketingPermission } from "../../../marketing/use-marketing-permissions";
import { TeamContext } from "../../team";
import {
  CancelBillingModal,
  ProductBillingCancellation,
  useBillingCancellationState,
} from "../billing/product-billing-cancellation";
import { RestartBillingPlanModal } from "../marketing/restart-billing-plan-modal";
import {
  ApplyOrderTrackingBillingToCurrentPeriodModal,
  proposedBillingFromForm,
} from "./apply-order-tracking-billing-modal";
import { OrderTrackingBillingPeriodChangeHint } from "./order-tracking-billing-period-hint";
import {
  orderTrackingBillingDiffersFromPeriodSnapshot,
  type OrderTrackingBillingPeriodComparePath,
  type OrderTrackingBillingPeriodSnapshot,
} from "./order-tracking-billing-period-utils";

export const orderTrackingBillingForm = groupInput({
  invoiceBilling: groupInput(
    {
      enabled: input<boolean>(),
      paymentProvider: input<PaymentProviderType>(),
      renewOnPeriodEnd: input<boolean>(),
      startDate: input<Date | null>({ equal: areDatesEqual }),
      period: input<BillingPeriod>(),
      periodFeeUsd: input<number>(),
      perOrderSettings: groupInput({
        enabled: input<boolean>(),
        includedOrders: input<number>(),
        overagePricePerOrderUsd: input<number>(),
      }),
      perShipmentSettings: groupInput({
        enabled: input<boolean>(),
        includedShipments: input<number>(),
        overagePricePerShipmentUsd: input<number>(),
      }),
      perSmsSettings: groupInput({
        enabled: input<boolean>(),
        includedCredits: input<number>(),
        overagePricePerSmsUsd: input<number>(),
        overagePricePerMmsUsd: input<number>(),
        chargeCarrierFees: input<boolean>(),
      }),
      perEmailSettings: groupInput({
        enabled: input<boolean>(),
        includedEmails: input<number>(),
        overagePricePerEmailUsd: input<number>(),
      }),
      /**
       * This has to be a separate field because evaluating it requires
       * knowing the team's timezone, which we don't have in the form validator.
       */
      startDateIsUpdatedToThePast: input<boolean>(),
    },
    {
      validator: (value) => {
        if (value.enabled && !value.startDate) {
          return ["Start date is required when invoice billing is enabled"];
        }
        if (value.startDateIsUpdatedToThePast) {
          return ["Start date cannot be in the past"];
        }
        return [];
      },
    },
  ),
  pricePerOrder: input<string>(),
  texts: groupInput({
    pricePerSms: input<string>(),
    pricePerMms: input<string>(),
  }),
});

type OrderTrackingBillingForm = InputSchema.Input<
  typeof orderTrackingBillingForm
>;

export type OrderTrackingBillingValue = InputSchema.Value<
  typeof orderTrackingBillingForm
>;

export const orderTrackingBillingDefault: OrderTrackingBillingValue = {
  invoiceBilling: {
    enabled: false,
    paymentProvider: PaymentProviderType.SHOPIFY,
    renewOnPeriodEnd: true,
    startDate: null,
    period: BillingPeriod.MONTHLY,
    periodFeeUsd: ORDER_TRACKING_BILLING_DEFAULTS.PERIOD_FEE_USD,
    perOrderSettings: {
      enabled: false,
      includedOrders: ORDER_TRACKING_BILLING_DEFAULTS.INCLUDED_ORDERS,
      overagePricePerOrderUsd:
        ORDER_TRACKING_BILLING_DEFAULTS.OVERAGE_PRICE_PER_ORDER_USD,
    },
    perShipmentSettings: {
      enabled: false,
      includedShipments: ORDER_TRACKING_BILLING_DEFAULTS.INCLUDED_SHIPMENTS,
      overagePricePerShipmentUsd:
        ORDER_TRACKING_BILLING_DEFAULTS.OVERAGE_PRICE_PER_SHIPMENT_USD,
    },
    perSmsSettings: {
      enabled: false,
      includedCredits: ORDER_TRACKING_BILLING_DEFAULTS.INCLUDED_CREDITS,
      overagePricePerSmsUsd:
        ORDER_TRACKING_BILLING_DEFAULTS.OVERAGE_PRICE_PER_SMS_USD,
      overagePricePerMmsUsd:
        ORDER_TRACKING_BILLING_DEFAULTS.OVERAGE_PRICE_PER_MMS_USD,
      chargeCarrierFees: false,
    },
    perEmailSettings: {
      enabled: false,
      includedEmails: ORDER_TRACKING_BILLING_DEFAULTS.INCLUDED_EMAILS,
      overagePricePerEmailUsd:
        ORDER_TRACKING_BILLING_DEFAULTS.OVERAGE_PRICE_PER_EMAIL_USD,
    },
    startDateIsUpdatedToThePast: false,
  },
  pricePerOrder: (0.06).toString(),
  texts: { pricePerSms: (0.01).toString(), pricePerMms: (0.03).toString() },
};

export const OrderTrackingBillingCard = memo(function OrderTrackingBillingCard({
  input,
  orderTrackingEnabled,
}: {
  input: OrderTrackingBillingForm;
  orderTrackingEnabled: boolean;
}) {
  const { invoiceBilling } = input.inputs;
  const isAuthorized = useHasMarketingPermission("marketing_admin");
  const team = useContext(TeamContext);
  const rpcClient = useRequiredContext(RedoAdminRpcClientContext);
  const activeSubscription = team?.settings?.shopifySubscription?.[0];
  const isEnabled =
    !!team?.settings.orderTracking?.billing?.invoiceBilling?.enabled;
  const teamTimezone = team?._shopify?.iana_timezone || "America/Los_Angeles";
  const now = getDateStartOfDayInTimezone(new Date(), teamTimezone);
  const cancellation = useBillingCancellationState(
    isEnabled ? team?._id : undefined,
    ServiceInvoiceType.ORDER_TRACKING,
  );
  const [renewOffModalOpen, setRenewOffModalOpen] = useState(false);

  // Turning renewal off mid-cycle has to say what happens to the open cycle.
  const setRenewOnPeriodEnd = (renew: boolean) => {
    if (
      !renew &&
      cancellation.state?.period &&
      !cancellation.state.cancellation
    ) {
      setRenewOffModalOpen(true);
      return;
    }
    invoiceBilling.inputs.renewOnPeriodEnd.setValue(renew);
  };
  const [restartOpen, setRestartOpen] = useState(false);

  const periodContextQuery = useRpcQuery(RedoAdminRpcClientContext, {
    method: "getOrderTrackingBillingPeriodContext",
    args: { teamId: team?._id ?? "" },
    options: { enabled: !!team?._id && isEnabled },
  });

  const periodSnapshot = periodContextQuery.data?.currentPeriodSnapshot as
    | OrderTrackingBillingPeriodSnapshot
    | null
    | undefined;
  const nextPeriodStart = periodContextQuery.data?.nextPeriodStart ?? null;
  const hasActiveBillingPeriod =
    !!periodContextQuery.data?.hasActivePeriod &&
    !!periodSnapshot &&
    !!nextPeriodStart;

  const [applyModalOpen, setApplyModalOpen] = useState(false);
  const [applyLoading, setApplyLoading] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState(false);
  const [previewCharges, setPreviewCharges] = useState<
    | {
        usageChargeUnderCurrentConfigUsd: number;
        usageChargeUnderProposedConfigUsd: number;
      }
    | undefined
  >(undefined);

  const invoiceBillingValue = useMemo(
    () => ({
      enabled: invoiceBilling.inputs.enabled.value,
      paymentProvider: invoiceBilling.inputs.paymentProvider.value,
      renewOnPeriodEnd: invoiceBilling.inputs.renewOnPeriodEnd.value,
      startDate: invoiceBilling.inputs.startDate.value,
      period: invoiceBilling.inputs.period.value,
      periodFeeUsd: invoiceBilling.inputs.periodFeeUsd.value,
      perOrderSettings: {
        enabled: invoiceBilling.inputs.perOrderSettings.inputs.enabled.value,
        includedOrders:
          invoiceBilling.inputs.perOrderSettings.inputs.includedOrders.value,
        overagePricePerOrderUsd:
          invoiceBilling.inputs.perOrderSettings.inputs.overagePricePerOrderUsd
            .value,
      },
      perShipmentSettings: {
        enabled: invoiceBilling.inputs.perShipmentSettings.inputs.enabled.value,
        includedShipments:
          invoiceBilling.inputs.perShipmentSettings.inputs.includedShipments
            .value,
        overagePricePerShipmentUsd:
          invoiceBilling.inputs.perShipmentSettings.inputs
            .overagePricePerShipmentUsd.value,
      },
      perSmsSettings: {
        enabled: invoiceBilling.inputs.perSmsSettings.inputs.enabled.value,
        includedCredits:
          invoiceBilling.inputs.perSmsSettings.inputs.includedCredits.value,
        overagePricePerSmsUsd:
          invoiceBilling.inputs.perSmsSettings.inputs.overagePricePerSmsUsd
            .value,
        overagePricePerMmsUsd:
          invoiceBilling.inputs.perSmsSettings.inputs.overagePricePerMmsUsd
            .value,
        chargeCarrierFees:
          invoiceBilling.inputs.perSmsSettings.inputs.chargeCarrierFees.value,
      },
      perEmailSettings: {
        enabled: invoiceBilling.inputs.perEmailSettings.inputs.enabled.value,
        includedEmails:
          invoiceBilling.inputs.perEmailSettings.inputs.includedEmails.value,
        overagePricePerEmailUsd:
          invoiceBilling.inputs.perEmailSettings.inputs.overagePricePerEmailUsd
            .value,
      },
      startDateIsUpdatedToThePast:
        invoiceBilling.inputs.startDateIsUpdatedToThePast.value,
    }),
    [invoiceBilling],
  );

  const billingDiffersFromPeriodSnapshot = useMemo(() => {
    if (!periodSnapshot) {
      return false;
    }
    return orderTrackingBillingDiffersFromPeriodSnapshot(
      invoiceBillingValue,
      periodSnapshot,
    );
  }, [invoiceBillingValue, periodSnapshot]);

  const hasUnsavedInvoiceBillingChanges = invoiceBilling.changed;

  const openApplyModal = useHandler(async () => {
    if (!team?._id) {
      return;
    }
    setPreviewCharges(undefined);
    setPreviewError(false);
    setPreviewLoading(true);
    setApplyModalOpen(true);
    try {
      const preview =
        await rpcClient.previewApplyOrderTrackingBillingToCurrentPeriod({
          teamId: team._id,
          proposed: proposedBillingFromForm(invoiceBillingValue),
        });
      setPreviewCharges(preview);
    } catch {
      setPreviewError(true);
      toast("We encountered an error estimating the current usage.", {
        variant: "error",
      });
    } finally {
      setPreviewLoading(false);
    }
  });

  const confirmApplyToCurrentPeriod = useHandler(async () => {
    if (!team?._id) {
      return;
    }
    setApplyLoading(true);
    try {
      await rpcClient.applyOrderTrackingBillingToCurrentPeriod({
        teamId: team._id,
        proposed: proposedBillingFromForm(invoiceBillingValue),
      });
      setApplyModalOpen(false);
      setPreviewError(false);
      await periodContextQuery.refetch();
    } catch {
      toast(
        "Failed to apply settings to the current period. Please try again.",
        { variant: "error" },
      );
    } finally {
      setApplyLoading(false);
    }
  });

  const onRestartStartDateSaved = useHandler((startDate: Date) => {
    invoiceBilling.inputs.startDate.setValue(startDate);
    void periodContextQuery.refetch();
  });

  const renderPeriodHint = (
    fieldPath: OrderTrackingBillingPeriodComparePath,
  ) => {
    if (!hasActiveBillingPeriod || !periodSnapshot || !nextPeriodStart) {
      return null;
    }
    return (
      <OrderTrackingBillingPeriodChangeHint
        fieldPath={fieldPath}
        invoiceBilling={invoiceBillingValue}
        nextPeriodStart={nextPeriodStart}
        snapshot={periodSnapshot}
      />
    );
  };

  const subscriptionAlreadyStarted = useMemo(() => {
    const { enabled, startDate } = invoiceBilling.inputs;
    const alreadyEnabled = !enabled.changed && enabled.value;

    if (!alreadyEnabled) {
      return false;
    }

    const startDateIsTodayOrBefore =
      !!startDate.value &&
      getDateStartOfDayInTimezone(startDate.value, teamTimezone).getTime() <=
        now.getTime();

    if (startDateIsTodayOrBefore) {
      return true;
    }

    return false;
    // FIXME
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teamTimezone]);

  const shouldDisableToggle = useMemo(() => {
    const enabled = invoiceBilling.inputs.enabled;
    const startDate = invoiceBilling.inputs.startDate;
    const currentlyEnabled = enabled.value;
    const startDateIsInPast =
      !!startDate.value &&
      getDateStartOfDayInTimezone(startDate.value, teamTimezone).getTime() <
        now.getTime();

    return currentlyEnabled && startDateIsInPast;
  }, [
    invoiceBilling.inputs.enabled,
    invoiceBilling.inputs.startDate,
    now,
    teamTimezone,
  ]);

  useEffect(() => {
    const startDate = invoiceBilling.inputs.startDate;
    const startDateIsUpdatedToThePast =
      invoiceBilling.inputs.startDateIsUpdatedToThePast;

    if (!startDate.changed || !startDate.value) {
      if (startDateIsUpdatedToThePast.value) {
        startDateIsUpdatedToThePast.setValue(false);
      }
      return;
    }

    const startDateIsInThePast =
      getDateStartOfDayInTimezone(startDate.value, teamTimezone).getTime() <
      getDateStartOfDayInTimezone(now, teamTimezone).getTime();

    if (startDateIsUpdatedToThePast.value !== startDateIsInThePast) {
      startDateIsUpdatedToThePast.setValue(startDateIsInThePast);
    }
    // FIXME
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoiceBilling, teamTimezone]);

  const invoiceBillingSettings = (
    <>
      <RedoToggle
        description="Automatically renew subscription at the end of each billing period"
        disabled={!isAuthorized}
        label="Renew when billing period ends"
        setValue={setRenewOnPeriodEnd}
        size="sm"
        value={invoiceBilling.inputs.renewOnPeriodEnd.value}
      />
      <LabeledInput
        description="We will charge the merchant their first period fee on this date."
        label="Start date"
      >
        <DateInput
          disabled={!isAuthorized || subscriptionAlreadyStarted}
          value={
            invoiceBilling.inputs.startDate.value
              ? (() => {
                  // Convert stored date (midnight in merchant timezone) back to a local date for display
                  const storedDate = invoiceBilling.inputs.startDate.value;
                  const zonedDateTime = Temporal.Instant.fromEpochMilliseconds(
                    storedDate.getTime(),
                  ).toZonedDateTimeISO(teamTimezone);
                  // Create a date in local timezone that represents the same calendar day
                  return new Date(
                    zonedDateTime.year,
                    zonedDateTime.month - 1,
                    zonedDateTime.day,
                  );
                })()
              : null
          }
          valueChange={(date) => {
            if (!date) {
              invoiceBilling.inputs.startDate.setValue(date);
            } else {
              invoiceBilling.inputs.startDate.setValue(
                getDateStartOfDayInTimezone(date, teamTimezone),
              );
            }
          }}
        />
      </LabeledInput>
      {team && isAuthorized && subscriptionAlreadyStarted && (
        <RedoButton
          hierarchy="secondary"
          onClick={() => setRestartOpen(true)}
          size="sm"
          text="Restart plan on a new date"
        />
      )}
      {team && restartOpen && (
        <RestartBillingPlanModal
          billingType={BillingType.ORDER_TRACKING}
          onClose={() => setRestartOpen(false)}
          onStartDateSaved={onRestartStartDateSaved}
          teamId={team._id}
          teamTimezone={teamTimezone}
        />
      )}
      <RedoRadioGroup<BillingPeriod>
        description="How often to bill for the monthly credit allowance"
        disabled={!isAuthorized}
        label="Billing period"
        options={Object.values(BillingPeriod).map((option) => ({
          id: option,
          label: billingPeriodToFriendlyUpper[option],
        }))}
        setValue={invoiceBilling.inputs.period.setValue}
        value={invoiceBilling.inputs.period.value}
      />
      <RedoRadioGroup<PaymentProviderType>
        description="Select which payment provider to use for billing"
        disabled={!isAuthorized}
        label="Payment provider"
        options={Object.values(PaymentProviderType).map((option) => ({
          id: option,
          label:
            option === PaymentProviderType.SHOPIFY
              ? "Shopify"
              : option === PaymentProviderType.STRIPE
                ? "Stripe"
                : assertNever(option),
        }))}
        setValue={invoiceBilling.inputs.paymentProvider.setValue}
        value={invoiceBilling.inputs.paymentProvider.value}
      />
      {invoiceBilling.inputs.paymentProvider.value ===
        PaymentProviderType.SHOPIFY && (
        <Text
          fontSize="xs"
          textColor={
            team?.settings.shopifySubscription?.[0]?.status === "ACTIVE"
              ? "success"
              : "warning"
          }
        >
          Shopify subscription:{" "}
          {team?.settings.shopifySubscription?.[0]?.status === "ACTIVE"
            ? "Active"
            : team?.settings.shopifySubscription?.[0]?.status
              ? `Not Active - ${team.settings.shopifySubscription[0].status}`
              : "Not Active"}
        </Text>
      )}
      {invoiceBilling.inputs.paymentProvider.value ===
        PaymentProviderType.STRIPE &&
        !teamGetPaymentMethod(team?.stripe ?? undefined) && (
          <Text fontSize="xs" textColor="warning">
            Team has no stripe payment method configured. Billing will fail
            until the team adds a payment method.
          </Text>
        )}
      <RedoIncrementDecrement
        disabled={!isAuthorized}
        fullWidth
        label="Billing period fee (USD)"
        min={0}
        prefix="$"
        setValue={invoiceBilling.inputs.periodFeeUsd.setValue}
        step={0.000_001}
        value={invoiceBilling.inputs.periodFeeUsd.value}
      />
      <RedoToggle
        description="Enable order-based billing"
        disabled={!isAuthorized}
        label="Bill per order"
        setValue={
          invoiceBilling.inputs.perOrderSettings.inputs.enabled.setValue
        }
        size="sm"
        value={invoiceBilling.inputs.perOrderSettings.inputs.enabled.value}
      />
      {renderPeriodHint("perOrderSettings.enabled")}
      {invoiceBilling.inputs.perOrderSettings.inputs.enabled.value && (
        <Flex dir="column" gap="xl" pl="xl">
          <RedoIncrementDecrement
            disabled={!isAuthorized}
            fullWidth
            label="Included orders per month"
            min={0}
            setValue={
              invoiceBilling.inputs.perOrderSettings.inputs.includedOrders
                .setValue
            }
            step={1}
            value={
              invoiceBilling.inputs.perOrderSettings.inputs.includedOrders.value
            }
          />
          {renderPeriodHint("perOrderSettings.includedOrders")}
          <RedoIncrementDecrement
            disabled={!isAuthorized}
            fullWidth
            label="Price per order (USD)"
            min={0}
            prefix="$"
            setValue={
              invoiceBilling.inputs.perOrderSettings.inputs
                .overagePricePerOrderUsd.setValue
            }
            step={0.000_001}
            value={
              invoiceBilling.inputs.perOrderSettings.inputs
                .overagePricePerOrderUsd.value
            }
          />
          {renderPeriodHint("perOrderSettings.overagePricePerOrderUsd")}
        </Flex>
      )}

      <RedoToggle
        description="Enable per-shipment billing (requires Order Tracking)"
        disabled={
          !isAuthorized || !orderTrackingEnabled
            ? ((!orderTrackingEnabled
                ? "Order Tracking must be enabled to bill per shipment"
                : undefined) ?? true)
            : false
        }
        label="Bill per shipment"
        setValue={
          invoiceBilling.inputs.perShipmentSettings.inputs.enabled.setValue
        }
        size="sm"
        value={invoiceBilling.inputs.perShipmentSettings.inputs.enabled.value}
      />
      {renderPeriodHint("perShipmentSettings.enabled")}
      {invoiceBilling.inputs.perShipmentSettings.inputs.enabled.value &&
        orderTrackingEnabled && (
          <Flex dir="column" gap="xl" pl="xl">
            <RedoIncrementDecrement
              disabled={!isAuthorized}
              fullWidth
              label="Included shipments per period"
              min={0}
              setValue={
                invoiceBilling.inputs.perShipmentSettings.inputs
                  .includedShipments.setValue
              }
              step={1}
              value={
                invoiceBilling.inputs.perShipmentSettings.inputs
                  .includedShipments.value
              }
            />
            {renderPeriodHint("perShipmentSettings.includedShipments")}
            <RedoIncrementDecrement
              disabled={!isAuthorized}
              fullWidth
              label="Overage price per shipment (USD)"
              min={0}
              prefix="$"
              setValue={
                invoiceBilling.inputs.perShipmentSettings.inputs
                  .overagePricePerShipmentUsd.setValue
              }
              step={0.000_001}
              value={
                invoiceBilling.inputs.perShipmentSettings.inputs
                  .overagePricePerShipmentUsd.value
              }
            />
            {renderPeriodHint("perShipmentSettings.overagePricePerShipmentUsd")}
          </Flex>
        )}

      <RedoToggle
        description="Enable SMS/MMS billing"
        disabled={!isAuthorized}
        label="Bill per SMS"
        setValue={invoiceBilling.inputs.perSmsSettings.inputs.enabled.setValue}
        size="sm"
        value={invoiceBilling.inputs.perSmsSettings.inputs.enabled.value}
      />
      {renderPeriodHint("perSmsSettings.enabled")}
      {invoiceBilling.inputs.perSmsSettings.inputs.enabled.value && (
        <Flex dir="column" gap="xl" pl="xl">
          <RedoIncrementDecrement
            disabled={!isAuthorized}
            fullWidth
            label="Included SMS credits per month"
            min={0}
            setValue={
              invoiceBilling.inputs.perSmsSettings.inputs.includedCredits
                .setValue
            }
            step={1}
            value={
              invoiceBilling.inputs.perSmsSettings.inputs.includedCredits.value
            }
          />
          {renderPeriodHint("perSmsSettings.includedCredits")}
          <RedoIncrementDecrement
            disabled={!isAuthorized}
            fullWidth
            label="Overage price per SMS (USD)"
            min={0}
            prefix="$"
            setValue={
              invoiceBilling.inputs.perSmsSettings.inputs.overagePricePerSmsUsd
                .setValue
            }
            step={0.000_001}
            value={
              invoiceBilling.inputs.perSmsSettings.inputs.overagePricePerSmsUsd
                .value
            }
          />
          {renderPeriodHint("perSmsSettings.overagePricePerSmsUsd")}
          <RedoIncrementDecrement
            disabled={!isAuthorized}
            fullWidth
            label="Overage price per MMS (USD)"
            min={0}
            prefix="$"
            setValue={
              invoiceBilling.inputs.perSmsSettings.inputs.overagePricePerMmsUsd
                .setValue
            }
            step={0.000_001}
            value={
              invoiceBilling.inputs.perSmsSettings.inputs.overagePricePerMmsUsd
                .value
            }
          />
          {renderPeriodHint("perSmsSettings.overagePricePerMmsUsd")}
          <RedoToggle
            description="Charge carrier fees"
            disabled={!isAuthorized}
            label="Charge for carrier fees"
            setValue={
              invoiceBilling.inputs.perSmsSettings.inputs.chargeCarrierFees
                .setValue
            }
            size="sm"
            value={
              invoiceBilling.inputs.perSmsSettings.inputs.chargeCarrierFees
                .value
            }
          />
          {renderPeriodHint("perSmsSettings.chargeCarrierFees")}
        </Flex>
      )}

      <RedoToggle
        description="Enable email billing"
        disabled={!isAuthorized}
        label="Bill per email"
        setValue={
          invoiceBilling.inputs.perEmailSettings.inputs.enabled.setValue
        }
        size="sm"
        value={invoiceBilling.inputs.perEmailSettings.inputs.enabled.value}
      />
      {renderPeriodHint("perEmailSettings.enabled")}
      {invoiceBilling.inputs.perEmailSettings.inputs.enabled.value && (
        <Flex dir="column" gap="xl" pl="xl">
          <RedoIncrementDecrement
            disabled={!isAuthorized}
            fullWidth
            label="Included emails per month"
            min={0}
            setValue={
              invoiceBilling.inputs.perEmailSettings.inputs.includedEmails
                .setValue
            }
            step={1}
            value={
              invoiceBilling.inputs.perEmailSettings.inputs.includedEmails.value
            }
          />
          {renderPeriodHint("perEmailSettings.includedEmails")}
          <RedoIncrementDecrement
            disabled={!isAuthorized}
            fullWidth
            label="Overage price per email (USD)"
            min={0}
            prefix="$"
            setValue={
              invoiceBilling.inputs.perEmailSettings.inputs
                .overagePricePerEmailUsd.setValue
            }
            step={0.000_001}
            value={
              invoiceBilling.inputs.perEmailSettings.inputs
                .overagePricePerEmailUsd.value
            }
          />
          {renderPeriodHint("perEmailSettings.overagePricePerEmailUsd")}
        </Flex>
      )}
      {hasActiveBillingPeriod &&
        isAuthorized &&
        billingDiffersFromPeriodSnapshot && (
          <DisabledTooltip
            disabledMessage={
              hasUnsavedInvoiceBillingChanges
                ? "Settings must be saved first."
                : undefined
            }
            wrapperClass="w-full"
          >
            <RedoButton
              className="w-full"
              disabled={hasUnsavedInvoiceBillingChanges}
              hierarchy="secondary"
              onClick={openApplyModal}
              text="Apply settings to current period"
            />
          </DisabledTooltip>
        )}
    </>
  );

  return (
    <>
      <Card title="Order tracking billing">
        <section className={(gridCss.grid, gridCss.gap1)}>
          <Flex className={gridCss.span6L} dir="column" gap="xl">
            {activeSubscription &&
              isEnabled &&
              team.settings.orderTracking?.billing?.invoiceBilling
                ?.paymentProvider === PaymentProviderType.SHOPIFY && (
                <div>
                  <h3>Active subscription</h3>
                  <Text as="p">Name: {activeSubscription?.name}</Text>
                  <Text as="p">Status: {activeSubscription?.status}</Text>
                </div>
              )}
            <RedoToggle
              disabled={!isAuthorized || shouldDisableToggle}
              label="Enabled"
              setValue={invoiceBilling.inputs.enabled.setValue}
              size="sm"
              value={invoiceBilling.inputs.enabled.value}
            />

            {invoiceBilling.inputs.enabled.value && (
              <Flex dir="column" gap="xl">
                {invoiceBillingSettings}
              </Flex>
            )}
            {isEnabled && team && (
              <ProductBillingCancellation
                disabled={!isAuthorized}
                onRenewalResumed={() =>
                  invoiceBilling.inputs.renewOnPeriodEnd.setValue(true)
                }
                productType={ServiceInvoiceType.ORDER_TRACKING}
                reload={cancellation.reload}
                state={cancellation.state}
                teamId={team._id}
                teamTimezone={teamTimezone}
              />
            )}
            {renewOffModalOpen && team && cancellation.state && (
              <CancelBillingModal
                onCancelled={() => {
                  setRenewOffModalOpen(false);
                  invoiceBilling.inputs.renewOnPeriodEnd.setValue(false);
                  cancellation.reload();
                }}
                onClose={() => setRenewOffModalOpen(false)}
                productType={ServiceInvoiceType.ORDER_TRACKING}
                state={cancellation.state}
                teamId={team._id}
                teamTimezone={teamTimezone}
              />
            )}
          </Flex>
        </section>
      </Card>
      <ApplyOrderTrackingBillingToCurrentPeriodModal
        loading={applyLoading}
        onClose={() => {
          setApplyModalOpen(false);
          setPreviewError(false);
        }}
        onConfirm={confirmApplyToCurrentPeriod}
        open={applyModalOpen}
        previewError={previewError}
        previewLoading={previewLoading}
        usageChargeUnderCurrentConfigUsd={
          previewCharges?.usageChargeUnderCurrentConfigUsd
        }
        usageChargeUnderProposedConfigUsd={
          previewCharges?.usageChargeUnderProposedConfigUsd
        }
      />
    </>
  );
});
